/*
    Examiner battery report page
*/

import React, { useCallback, useEffect, useState } from 'react';
import Dashboard from '../dashboard';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import styles from '../../style/info-card.module.css';
import { Button, Select, notification } from 'antd';
import Table from '../table';

// PIN-231: the label alone is whatever free text was typed when the rater was
// invited, so lead with the rater's name (same precedence as print-responses.js).
const formatRecordOption = (record) => {
  const raterName = record.data?.respondent_name || record.data?.rater_fullName;
  const date = record.testDate ? ` (${new Date(record.testDate).toLocaleDateString()})` : '';
  return raterName ? `${raterName} — ${record.label}${date}` : `${record.label}${date}`;
};

const ALL_NORMS = ['Male', 'Female', 'Combined'];

// PIN-337: the norms every selected record's test offers. A test the API lists
// no choices for (an older API) keeps all three.
const normsOptionsFor = (normsGenderOptions = {}, testIDs) =>
  ALL_NORMS.filter((norms) =>
    testIDs.every((testID) => (normsGenderOptions[testID] ?? ALL_NORMS).includes(norms))
  );

// PIN-229: a failed load says so on the page — except a 401: the session is gone,
// and the error middleware already signs the examiner out and says so.
const notifyLoadFailure = (error, message) => {
  if (error?.response?.status === 401) return;
  notification['error']({ message, description: 'Reload the page to try again.' });
};

const BatteryReportPage = () => {
  const { batteryID } = useParams();
  const token = useSelector((state) => state.token);
  const [batteryInfo, setBatteryInfo] = useState({});
  const [reports, setReports] = useState([]); // [{...report, label(name), value(_id)}] added for Table
  const [selectedReportID, setSelectedReportID] = useState();
  const [examinees, setExaminees] = useState([]); // [{...examinee, label(label: first + last), value(_id)}] added for Table
  const [selectedExamineeID, setSelectedExamineeID] = useState();
  const [selectedNormsGender, setSelectedNormsGender] = useState();
  const [records, setRecords] = useState({}); // {testID: [records]}
  const [selectedRecords, setSelectedRecords] = useState({}); // {testID: recordID}
  // cost/affordability preview for the selected report+record(s): { alreadyUnlocked, cost, creditsAvailable, affordable }
  const [preview, setPreview] = useState(null);

  const getBatteryInfo = useCallback(async () => {
    if (!token) return;

    const data = {
      batteryID: batteryID
    };

    const config = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Token ' + token
      },
      // PIN-229: this page's loads show their own message when they fail, so the
      // error middleware's generic toast would only stack on top of it.
      _silent: true
    };

    let loadError;
    let response = await axios
      .post(process.env.REACT_APP_API_URL + '/battery/get', data, config)
      .catch((error) => {
        console.log(error);
        loadError = error;
      });

    if (!response) {
      notifyLoadFailure(loadError, 'Could not load this battery.');
      return;
    }

    setBatteryInfo({
      name: response.data.battery.name,
      // PIN-198 #11: the Male/Female/Combined norms selector only applies to
      // batteries whose scoring engine reads normsGender (STARS and MEFS —
      // see wici-api's helpers/norms-gender.js). WCAT and ANCR have no
      // gendered norms, so the dropdown is hidden for them rather than
      // offered and silently ignored.
      supportsNormsGender: !!response.data.battery.supportsNormsGender,
      normsGenderOptions: response.data.battery.normsGenderOptions
    });
  }, [batteryID, token]);

  const getExaminees = useCallback(async () => {
    // Signing out clears the token, which re-runs this; without one it can only be refused.
    if (!token) return;

    let loadError;
    let response = await axios
      .get(process.env.REACT_APP_API_URL + '/examinee/directory', {
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Token ' + token
        },
        _silent: true
      })
      .catch((error) => {
        console.log(error);
        loadError = error;
      });

    if (!response) {
      notifyLoadFailure(loadError, 'Could not load examinees.');
      return;
    }

    let result = [];

    response.data.examinees.forEach((examinee) => {
      result.push({
        ...examinee,
        label: `${examinee.label}: ${examinee.firstName} ${examinee.lastName}`,
        value: examinee._id
      });
    });

    setExaminees(result);
  }, [token]);

  const getDefaultCreditPoolID = useCallback(async () => {
    const config = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Token ' + token
      }
    };

    let response = await axios
      .post(process.env.REACT_APP_API_URL + '/user/info', {}, config)
      .catch((error) => {
        console.log(error);
      });

    // notify user if default credit pool has not been set
    if (!response || !response.data.defaultCreditPool) {
      notification['warning']({
        message: 'No default credit pool selected.',
        description:
          "Navigate to 'Credits' from the user dropdown, then create and/or set a credit pool as your default."
      });
    }
  }, [token]);

  // Fetch the cost/affordability preview for the selected report+record(s). Replaces the retired
  // /transaction/purchased check — the credit is charged automatically on report generation, so
  // the UI only needs to tell the examiner what generating will cost before they commit.
  const getPreview = useCallback(async () => {
    const recordIDs = Object.values(selectedRecords);

    if (!selectedReportID || recordIDs.length === 0) {
      setPreview(null);
      return;
    }

    let data = {
      reportID: selectedReportID,
      recordIDs
    };

    const config = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Token ' + token
      }
    };

    const response = await axios
      .post(process.env.REACT_APP_API_URL + '/transaction/preview', data, config)
      .catch((error) => {
        console.log(error);
      });

    setPreview(response ? response.data : null);
  }, [selectedReportID, selectedRecords, token]);

  // Resolves to { records: {testID: [records]} }, or { failed: true, error } if the request
  // failed. It doesn't set `records` itself: the effect that calls it knows whether its
  // selection is still current.
  const getRecordsForReport = useCallback(async () => {
    if (!token || !selectedExamineeID || !selectedReportID) return { records: {} };

    const data = {
      examineeID: selectedExamineeID,
      reportID: selectedReportID
    };

    const config = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Token ' + token
      },
      _silent: true
    };

    let loadError;
    let response = await axios
      .post(process.env.REACT_APP_API_URL + '/record/getRecordsForReport', data, config)
      .catch((error) => {
        console.log(error);
        loadError = error;
      });

    if (!response) return { failed: true, error: loadError };

    let result = {};
    response.data.records.forEach((record) => {
      if (!result[record.test._id]) result[record.test._id] = [];

      let recordsForTest = result[record.test._id];
      recordsForTest.push(record);
      result[record.test._id] = recordsForTest;
    });

    return { records: result };
  }, [selectedExamineeID, selectedReportID, token]);

  const getReports = useCallback(async () => {
    if (!token) return;

    const data = {
      batteryID
    };

    const config = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Token ' + token
      },
      _silent: true
    };

    let loadError;
    let response = await axios
      .post(process.env.REACT_APP_API_URL + '/report/list', data, config)
      .catch((error) => {
        console.log(error);
        loadError = error;
      });

    if (!response) {
      notifyLoadFailure(loadError, 'Could not load reports.');
      return;
    }

    let result = [];

    response.data.reports.forEach((report) => {
      result.push({
        ...report,
        label: report.name,
        value: report._id
      });
    });

    setReports(result);
  }, [batteryID, token]);

  const makeRecordSelect = useCallback(
    (testID) => {
      return (
        <Select
          key={JSON.stringify(testID)}
          allowClear
          showSearch
          optionFilterProp="children"
          style={{ width: '100%' }}
          placeholder="Select Record"
          // PIN-229: controlled, so clearing selectedRecords when the report or
          // examinee changes also clears what this dropdown shows.
          value={selectedRecords[testID]}
          onChange={(value) => {
            let newSlectedRecords = {
              ...selectedRecords,
              [testID]: value
            };

            if (value === undefined) delete newSlectedRecords[testID];

            setSelectedRecords(newSlectedRecords);
          }}
        >
          {records[testID].map((record) => {
            return <Select.Option key={record._id}>{formatRecordOption(record)}</Select.Option>;
          })}
        </Select>
      );
    },
    [records, selectedRecords]
  );

  const makeRecordTableData = useCallback(() => {
    let result = [];

    if (!records) return result;

    for (const [test, recs] of Object.entries(records)) {
      let row = {};
      row.test = recs.at(0).test.name;
      row.record = makeRecordSelect(test);
      result.push(row);
    }

    return result;
  }, [makeRecordSelect, records]);

  const renderRecordTable = useCallback(() => {
    let data = makeRecordTableData();

    return (
      <Table
        canAdd={false}
        canDelete={false}
        canModify={false}
        columns={[
          {
            dataKey: 'test',
            title: 'Test',
            type: 'input'
          },
          {
            dataKey: 'record',
            title: 'Record',
            type: 'node'
          }
        ]}
        data={data}
        dataDidChange={() => {}}
        isEditing={true}
      />
    );
  }, [makeRecordTableData]);

  // PIN-337: the choices can differ by test (MEFS offers Male and Female only,
  // PIN-359), so they narrow to the selected records' tests, and a choice they
  // don't offer no longer counts.
  const normsOptions = normsOptionsFor(
    batteryInfo.normsGenderOptions,
    Object.keys(selectedRecords)
  );
  const normsGender = normsOptions.includes(selectedNormsGender) ? selectedNormsGender : undefined;

  const renderReportButton = useCallback(() => {
    let recordIDs = Object.values(selectedRecords);

    // Norms selection only gates batteries that actually offer it (PIN-198
    // #11) — otherwise a battery whose dropdown is hidden could never
    // satisfy this condition and Generate Report would stay disabled forever.
    const hasSelection =
      selectedExamineeID &&
      selectedReportID &&
      (!batteryInfo.supportsNormsGender || normsGender) &&
      Object.keys(recordIDs).length > 0;
    const alreadyUnlocked = preview ? preview.alreadyUnlocked : false;
    const unaffordable = preview ? !preview.affordable && !preview.alreadyUnlocked : false;

    // The charge happens automatically when the report is generated, so the button just
    // navigates to the report — no separate purchase step. We disable it until selections are
    // made, the preview has loaded, or when the examiner can't afford the credit.
    let label = 'Generate Report';
    if (!hasSelection) label = 'Select';
    else if (unaffordable) label = 'Insufficient credits';
    else if (alreadyUnlocked) label = 'View';

    const cost = preview ? preview.cost : null;
    const title =
      hasSelection && preview
        ? alreadyUnlocked
          ? 'Already unlocked — generating is free'
          : `Generating will cost ${cost} credit${cost === 1 ? '' : 's'} (${preview.creditsAvailable} available)`
        : undefined;

    return (
      <Button
        block
        className={styles['ant-btn']}
        danger={unaffordable}
        disabled={!hasSelection || preview === null || unaffordable}
        onClick={() => {
          const query = normsGender ? `?normsGender=${encodeURIComponent(normsGender)}` : '';
          window.open(
            `${window.location.origin}/report/${selectedReportID}/records/${recordIDs}${query}`,
            '_blank'
          );
        }}
        title={title}
        type="primary"
      >
        {label}
      </Button>
    );
  }, [
    preview,
    selectedRecords,
    selectedExamineeID,
    selectedReportID,
    normsGender,
    batteryInfo.supportsNormsGender
  ]);

  useEffect(() => {
    getBatteryInfo();
    getDefaultCreditPoolID();
    getReports();
    getExaminees();
  }, [getBatteryInfo, getDefaultCreditPoolID, getReports, getExaminees]);

  /*
    PIN-229: a slower response for an earlier report/examinee must not overwrite
    the current selection's records, so only the latest run applies its result.
  */
  useEffect(() => {
    let ignore = false;

    // Nothing from the previous selection may be picked while this one's records load:
    // a pick made in that gap would survive into a report for the wrong examinee.
    setRecords({});
    setSelectedRecords({});

    getRecordsForReport().then((result) => {
      if (ignore) return;

      if (result.failed) {
        notifyLoadFailure(result.error, 'Could not load records.');
        return;
      }

      setRecords(result.records);
    });

    return () => {
      ignore = true;
    };
  }, [getRecordsForReport]);

  useEffect(() => {
    getPreview();
  }, [getPreview]);

  return (
    <Dashboard>
      <div className={styles['card']}>
        <div className={styles['header']}>
          <label className={styles['name']}>{batteryInfo.name ? batteryInfo.name : null}</label>
        </div>
        <hr className={styles['horizontal-line']}></hr>
        <div className={styles['info-container']}>
          <div className={styles['item-container']} height-50="true" width-80="true">
            <label className={styles['item-name']}>Generate Report</label>
            <div
              className={styles['item-container']}
              flex-direction_row="true"
              height-100="true"
              width-100="true"
              style={{ width: '100%' }}
            >
              {' '}
              {/* width needs to be set here. it currently sets flex basis */}
              <div
                className={styles['item-container']}
                height-100="true"
                width-50="true"
                justify-content_flex-start="true"
              >
                <Select
                  allowClear
                  style={{ width: '100%' }}
                  placeholder="Select Report"
                  onChange={(value) => {
                    setSelectedRecords({});
                    setSelectedReportID(value);
                  }}
                  options={reports}
                />
                <Select
                  allowClear
                  style={{ width: '100%' }}
                  placeholder="Select Examinee"
                  onChange={(value) => {
                    setSelectedRecords({});
                    setSelectedExamineeID(value);
                  }}
                  options={examinees}
                />
                {batteryInfo.supportsNormsGender && (
                  <Select
                    allowClear
                    style={{ width: '100%' }}
                    placeholder="Select Norms"
                    value={normsGender}
                    onChange={setSelectedNormsGender}
                    options={normsOptions.map((norms) => ({ label: norms, value: norms }))}
                  />
                )}
                {renderReportButton()}
              </div>
              <div
                className={styles['item-container']}
                height-100="true"
                width-50="true"
                justify-content_flex-start="true"
              >
                {renderRecordTable()}
              </div>
            </div>
            <hr className={styles['item-underline']}></hr>
          </div>
        </div>
      </div>
    </Dashboard>
  );
};

export default BatteryReportPage;
